<template>
    <div class="filter-panel">
        <b-form @submit.prevent="onSubmit" v-test:form>
            <b-form-group
                :label="title"
                v-slot="{ ariaDescribedby }"
            >
                <component
                    :is="type === 'multiple' ? 'b-form-checkbox-group' : 'b-form-radio-group'"
                    v-model="selected"
                    :aria-describedby="ariaDescribedby"
                    class="filter-list"
                >
                    <component
                        v-for="filterItem in filters"
                        :is="type === 'multiple' ? 'b-form-checkbox' : 'b-form-radio'"
                        :key="filterItem.value"
                        :value="filterItem.value"
                        class="filter-item"
                    >
                        <option-icon
                            :text="filterItem.text"
                            :subtext="filterItem.subtext"
                            :image="filterItem.image"
                            v-test:optionIcon
                        />
                    </component>
                </component>
            </b-form-group>

            <div class="btn-container">
                <b-button
                    variant="primary"
                    type="submit"
                    class="apply-btn"
                    block
                    v-test:applyBtn
                >
                    {{ $snippet('apply', 'apply') }}
                </b-button>
            </div>
        </b-form>
    </div>
</template>

<script>
    import {
        BForm,
        BFormGroup,
        BFormCheckboxGroup,
        BFormCheckbox,
        BFormRadioGroup,
        BFormRadio,
    } from 'bootstrap-vue';
    import createPropProxy from '../../../lib/prop-proxies';
    import OptionIcon from '../../atoms/OptionIcon/OptionIcon';

    export default {
        name: 'filter-panel',

        components: {
            BForm,
            BFormGroup,
            BFormCheckboxGroup,
            BFormCheckbox,
            BFormRadioGroup,
            BFormRadio,
            OptionIcon,
        },

        props: {
            title: {
                type: String,
                default: '',
            },

            type: {
                type: String,
                default: 'multiple',
            },

            filters: {
                type: Array,
                default: () => [],
            },

            selectedFilters: {
                type: Array,
                default: () => [],
            },
        },

        data() {
            /**
             * Create local copy of selected filters so events aren't emitted until the submit button clicked.
             */
            return {
                selected: [],
            };
        },

        computed: {
            /**
             * Create selectedFiltersProxy.
             *
             * @returns {object}
             */
            ...createPropProxy(['selectedFilters']),
        },

        watch: {
            /**
             * Watch if the value prop changes and update the selected value.
             * This is needed when persisting filter selections after refresh.
             * The data will be passed down and applied to the filter panel.
             */
            value: {
                handler() {
                    if (this.type === 'multiple') {
                        this.selected = this.selectedFilters;
                    } else {
                        this.selected = this.selectedFilters[0];
                    }
                },
                immediate: true,
            },
        },

        methods: {
            /**
             * On submit, emit event with selected filters.
             * The selectedFilterProxy will automatically emit an update:selectedFilters event when it changes.
             */
            onSubmit() {
                if (!Array.isArray(this.selected)) {
                    this.selectedFiltersProxy = [this.selected];
                } else {
                    this.selectedFiltersProxy = this.selected;
                }
                this.$emit('close-panel');
            },
        },
    };
</script>

<style lang="scss" scoped>
    ::v-deep {
        @import "~bootstrap/scss/custom-forms";
    }

    ::v-deep legend {
        border-bottom: solid 2px primary-palette(2);
        margin-bottom: 20px;
        font-weight: 700;
        font-size: rem(32px);
        line-height: rem(36px);
        padding-bottom: 8px;
    }

    .filter-panel {
        container-type: inline-size;
    }

    .filter-list {
        display: flex;
        flex-wrap: wrap;
        gap: 10px 4%;
    }

    .filter-item {
        display: flex;
        align-items: center;
        position: relative;
        padding-left: 0;
        min-height: 32px;
        margin: 0;
    }

    @container (min-width: 540px) {
        .filter-list {
            flex-direction: row;
            align-items: flex-start;
        }

        .filter-item {
            flex: 0 0 48%;
        }
    }

    @container (max-width: 539px) {
        .filter-list {
            flex-direction: column;
            align-items: flex-stretch;
        }

        .filter-item {
            flex: 0 0 100%;
        }
    }

    ::v-deep .custom-control-label {
        width: 100%;
        padding-right: 40px;
        display: flex;
        align-items: center;
        min-height: 2em;

        &::before,
        &::after {
            left: unset;
            right: 0;
            top: 4px;
        }
    }

    .btn-container {
        padding-top: 20px;
    }
</style>
